<template>
  <div class="list-container">
    <div class="list-header">
      <h1>Roles & Permissions Admin</h1>
    </div>

    <div class="tabs">
      <button 
        :class="{ 'active': activeTab === 'roles' }" 
        @click="activeTab = 'roles'"
      >
        Organization Roles
      </button>
      <button 
        :class="{ 'active': activeTab === 'matrix' }" 
        @click="activeTab = 'matrix'"
      >
        Permissions Matrix
      </button>
    </div>

    <!-- Roles Tab -->
    <div v-if="activeTab === 'roles'" class="tab-content">
      <div class="list-header">
        <h2>Organization Roles</h2>
        <router-link to="/admin/permissions/roles/new" class="important-button big">
          <font-awesome-icon icon="plus" /> New Role
        </router-link>
      </div>

      <LoadingIndicator v-if="isLoading">Loading roles...</LoadingIndicator>
      <table v-else>
        <thead>
          <tr>
            <th>Name</th>
            <th>Description</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="role in sortedRoles" :key="role.id">
            <td>{{ role.name }}</td>
            <td class="overflow">{{ role.description }}</td>
            <td>
              <span class="actions">
                <router-link
                  :to="`/admin/permissions/roles/${role.id}/edit`"
                  class="action-button edit"
                  title="Edit"
                >
                  <font-awesome-icon icon="edit" />
                </router-link>
                <button
                  @click="confirmDeleteRole(role)"
                  class="action-button delete"
                  title="Delete"
                >
                  <font-awesome-icon icon="trash" />
                </button>
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Permissions Matrix Tab -->
    <div v-if="activeTab === 'matrix'" class="tab-content">
      <div class="list-header">
        <h2>Permissions Matrix</h2>
      </div>

      <LoadingIndicator v-if="isLoading">Loading matrix...</LoadingIndicator>
      <div v-else class="matrix-container">
        <table class="matrix-table">
          <thead>
            <tr>
              <th></th> <!-- Empty corner cell -->
              <th v-for="role in sortedRoles" :key="role.id" :title="role.description" class="matrix-header">
                {{ role.name }}
                <div class="matrix-subheader">Organization</div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="permission in sortedPermissions" :key="permission.id">
              <td class="matrix-header">
                {{ permission.name }}
                <div class="matrix-subheader">{{ permission.description }}</div>
              </td>
              <td v-for="role in sortedRoles" :key="role.id" class="matrix-cell">
                <input 
                  type="checkbox"
                  :checked="hasPermission(role, permission)"
                  @change="togglePermission(role, permission)"
                  :disabled="isSystemPermission(permission)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { FontAwesomeIcon } from '@/scripts/icons';
import { handleApiError, showConfirmDialog } from '@/scripts/shared-utils';
import { sortRoles, sortPermissions, isSystemPermission } from '@/scripts/permissions';
import { getRoles, deleteRole, getRolePermissions, updateRolePermissions, roleKeys } from '@/scripts/api/rolesApi';
import { getPermissions, permissionKeys } from '@/scripts/api/permissionsApi';
import LoadingIndicator from '@/components/LoadingIndicator.vue';

const queryClient = useQueryClient();
const activeTab = ref('roles');

// --- DATA FETCHING ---
const { data: roles, isLoading: isLoadingRoles } = useQuery({
  queryKey: roleKeys.getRoles(),
  queryFn: getRoles,
  staleTime: 60000,
  placeholderData: [],
  onError: (error) => handleApiError(error, 'fetching roles')
});

const { data: permissions, isLoading: isLoadingPermissions } = useQuery({
  queryKey: permissionKeys.getPermissions(),
  queryFn: getPermissions,
  staleTime: 60000,
  placeholderData: [],
  onError: (error) => handleApiError(error, 'fetching permissions')
});

watch(roles, (newRoles) => {
  if (newRoles) {
    newRoles.forEach(role => {
      queryClient.prefetchQuery({
        queryKey: roleKeys.getRolePermissions(role.id),
        queryFn: () => getRolePermissions(role.id),
      });
    });
  }
}, { immediate: true });

const getRolePerms = (roleId) => {
  const data = queryClient.getQueryData(roleKeys.getRolePermissions(roleId));
  return new Set(data?.map(p => p.id) || []);
};

const isLoading = computed(() => isLoadingRoles.value || isLoadingPermissions.value);

// --- COMPUTED PROPERTIES ---
const sortedRoles = computed(() => sortRoles(roles.value));
const sortedPermissions = computed(() => sortPermissions(permissions.value));

// --- MUTATIONS ---
const deleteRoleMutation = useMutation({
  mutationFn: deleteRole,
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: roleKeys.getRoles() });
  },
  onError: (error) => handleApiError(error, 'deleting role'),
});

const updatePermissionsMutation = useMutation({
  mutationFn: ({ roleId, permissionIds }) => updateRolePermissions(roleId, permissionIds),
  onSuccess: (variables) => {
    // Update the cache with the permissions we just saved
    queryClient.setQueryData(roleKeys.getRolePermissions(variables.roleId), variables.permissionIds);
    // Invalidate to ensure consistency with the server
    queryClient.invalidateQueries({ queryKey: roleKeys.getRolePermissions(variables.roleId) });
  },
  onError: (error) => handleApiError(error, 'updating permissions'),
});

// --- METHODS ---
const confirmDeleteRole = (role) => {
  if (showConfirmDialog(`Are you sure you want to delete the role "${role.name}"?`)) {
    deleteRoleMutation.mutate(role.id);
  }
};

const hasPermission = (role, permission) => {
  const rolePerms = getRolePerms(role.id);
  return rolePerms.has(permission.id);
};

const togglePermission = (role, permission) => {
  const currentPerms = Array.from(getRolePerms(role.id));
  const permissionId = permission.id;
  const newPermissionIds = currentPerms.includes(permissionId)
    ? currentPerms.filter(id => id !== permissionId)
    : [...currentPerms, permissionId];

  updatePermissionsMutation.mutate({ roleId: role.id, permissionIds: newPermissionIds });
};
</script>

<style scoped>
@import '@/styles/shared-list.css';
@import '@/styles/shared-tabs.css';
@import '@/styles/shared-matrix.css';
</style>
